import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useLocation } from 'wouter'
import { ArrowLeft, AlertTriangle, Shield, Info } from 'lucide-react'

export default function DisclaimerPage() {
  const [, navigate] = useLocation()

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="text-3xl font-bold text-center flex items-center justify-center gap-3">
                <AlertTriangle className="h-8 w-8 text-amber-500" />
                Disclaimer
              </CardTitle>
              <p className="text-muted-foreground text-center">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </CardHeader>
            <CardContent className="prose max-w-none space-y-6">
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Important Notice</h3>
                    <p className="text-amber-700 dark:text-amber-300 text-sm">
                      Please read this disclaimer carefully before using PrizeDrop. By accessing and using our platform, you acknowledge that you have read, understood, and agree to the terms outlined below.
                    </p>
                  </div>
                </div>
              </div>

              <section>
                <h2 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                  <Shield className="h-6 w-6" />
                  1. General Disclaimer
                </h2>
                <p>
                  The information provided on PrizeDrop is for general informational purposes only. While we strive to keep the information up to date and accurate, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the website or the information, products, services, or related graphics contained on the website for any purpose.
                </p>
                <p className="mt-3">
                  Any reliance you place on such information is therefore strictly at your own risk. In no event will we be liable for any loss or damage including without limitation, indirect or consequential loss or damage, or any loss or damage whatsoever arising from loss of data or profits arising out of, or in connection with, the use of this website.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">2. Competition Participation</h2>
                <h3 className="text-xl font-semibold mb-2">No Guarantee of Winning</h3>
                <p>
                  Participation in PrizeDrop competitions does not guarantee that you will win any prizes. All competitions are subject to chance (unless otherwise specified as skill-based), and winners are selected through random draws or other specified methods. The odds of winning depend on the number of eligible entries received for each competition.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Entry Requirements</h3>
                <p>
                  Participants must meet all eligibility requirements as outlined in our Terms and Conditions. It is your responsibility to ensure you are eligible to participate in competitions according to your local laws and regulations. PrizeDrop reserves the right to verify eligibility and disqualify participants who do not meet the requirements.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Prize Fulfillment</h3>
                <p>
                  While we make every effort to deliver prizes as described, PrizeDrop does not guarantee the availability of specific prizes. We reserve the right to substitute prizes of equal or greater value if the advertised prize becomes unavailable. Prize values are estimates and may vary based on market conditions.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">3. Third-Party Services and Content</h2>
                <h3 className="text-xl font-semibold mb-2">Partner Services</h3>
                <p>
                  Some prizes may involve services or products provided by third-party partners (such as digital vouchers, airtime vouchers, or educational scholarships). PrizeDrop is not responsible for the quality, availability, or terms of service of these third-party offerings. Any issues with third-party prizes must be resolved directly with the relevant provider.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">External Links</h3>
                <p>
                  Our platform may contain links to external websites or services. These links are provided for convenience and informational purposes only. PrizeDrop does not endorse, control, or assume responsibility for the content, privacy policies, or practices of any third-party websites or services.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Third-Party Platform Disclaimers</h3>
                <p>
                  Some prizes may include digital vouchers or promotional credits for various platforms. These are provided as promotional gifts and are subject to the terms and conditions of the respective platforms. Recipients must be 18 or older and should use these services responsibly. PrizeDrop does not promote any specific activities and is not responsible for any losses or issues that may arise from third-party platform usage.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">4. Technical Limitations</h2>
                <h3 className="text-xl font-semibold mb-2">Platform Availability</h3>
                <p>
                  We strive to maintain platform availability but cannot guarantee uninterrupted access. The platform may be temporarily unavailable due to maintenance, updates, technical issues, or circumstances beyond our control. We are not liable for any inconvenience or losses resulting from platform downtime.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Data Accuracy</h3>
                <p>
                  While we make efforts to ensure data accuracy, technical errors may occur in competition information, prize descriptions, or entry processing. If discrepancies arise, the official competition rules and terms take precedence over any conflicting information displayed on the platform.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Mobile and Browser Compatibility</h3>
                <p>
                  The platform is designed to work across various devices and browsers, but we cannot guarantee optimal performance on all combinations. Users are responsible for ensuring their devices and software are compatible with our platform requirements.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">5. Financial Disclaimers</h2>
                <h3 className="text-xl font-semibold mb-2">No Financial Advice</h3>
                <p>
                  Any information about prizes, investments, or financial opportunities is for informational purposes only and should not be considered financial advice. Participants should conduct their own research and consult with qualified financial advisors before making any financial decisions.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Tax Implications</h3>
                <p>
                  Winners are responsible for understanding and complying with all tax obligations related to prize acceptance. Tax laws vary by jurisdiction, and while prizes may generally not be subject to income tax in South Africa, winners should consult tax professionals for guidance specific to their circumstances.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Currency and Exchange Rates</h3>
                <p>
                  Prize values are typically stated in South African Rand (ZAR). If prizes are provided in other currencies or converted to other currencies, exchange rates may affect the actual value received. PrizeDrop is not responsible for currency fluctuations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">6. Privacy and Data Protection</h2>
                <h3 className="text-xl font-semibold mb-2">Data Processing</h3>
                <p>
                  While we implement security measures to protect your data, no online platform can guarantee complete security. Users provide personal information at their own risk and should review our Privacy Policy for detailed information about data handling practices.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Data Sharing</h3>
                <p>
                  Your participation may involve sharing certain information with competition partners, auditors, and service providers as outlined in our Privacy Policy. By participating, you acknowledge and consent to such data sharing for legitimate competition purposes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">7. Legal and Regulatory Compliance</h2>
                <h3 className="text-xl font-semibold mb-2">Jurisdictional Variations</h3>
                <p>
                  Competition laws and regulations vary by jurisdiction. While our competitions are designed to comply with South African law, participants from other jurisdictions are responsible for ensuring their participation complies with their local laws.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Age Restrictions</h3>
                <p>
                  Our platform is strictly for users 18 years and older. We implement age verification measures, but users are ultimately responsible for providing accurate age information. Participation by minors is prohibited and will result in disqualification.
                </p>

                <h3 className="text-xl font-semibold mb-2 mt-4">Regulatory Changes</h3>
                <p>
                  Competition regulations may change, potentially affecting ongoing competitions. We reserve the right to modify or cancel competitions to ensure compliance with applicable laws and regulations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">8. Limitation of Liability</h2>
                <p>
                  To the fullest extent permitted by law, PrizeDrop, its directors, employees, partners, and affiliates shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Use or inability to use the platform</li>
                  <li>Any errors or omissions in platform content</li>
                  <li>Unauthorized access to or alteration of your data</li>
                  <li>Third-party conduct or content on the platform</li>
                  <li>Any interruption, delay, or failure in platform services</li>
                  <li>Loss of profits, data, or other intangible losses</li>
                </ul>
                <p className="mt-3">
                  Our total liability for any claims arising from your use of the platform shall not exceed the value of any prizes you may have won through our competitions.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">9. Indemnification</h2>
                <p>
                  By using PrizeDrop, you agree to indemnify and hold harmless PrizeDrop and its affiliates from any claims, damages, losses, or expenses (including legal fees) arising from:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Your use of the platform</li>
                  <li>Your violation of these terms or applicable laws</li>
                  <li>Your infringement of any third-party rights</li>
                  <li>Any content you submit to the platform</li>
                  <li>Your participation in competitions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">10. Changes to This Disclaimer</h2>
                <p>
                  We reserve the right to modify this disclaimer at any time. Changes will be posted on this page with an updated date. Your continued use of the platform after changes are posted constitutes acceptance of the updated disclaimer. We encourage you to review this disclaimer periodically to stay informed of any updates.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">11. Governing Law</h2>
                <p>
                  This disclaimer is governed by South African law. Any disputes arising from this disclaimer or your use of the platform will be subject to the exclusive jurisdiction of South African courts, except where consumer protection laws provide otherwise.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">12. Contact Information</h2>
                <p>
                  If you have any questions about this disclaimer or need clarification on any points, please contact us:
                </p>
                <div className="mt-3 space-y-1">
                  <p><strong>Email:</strong> legal@prizedrop.co.za</p>
                  <p><strong>General Support:</strong> support@prizedrop.co.za</p>
                  <p><strong>Website:</strong> www.prizedrop.co.za</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-3">13. Acknowledgment</h2>
                <p>
                  By using PrizeDrop, you acknowledge that you have read this disclaimer, understand its contents, and agree to its terms. You also acknowledge that this disclaimer should be read in conjunction with our Terms and Conditions and Privacy Policy, which together form the complete agreement between you and PrizeDrop.
                </p>
              </section>

              <div className="border-t pt-6 mt-8 bg-slate-50 dark:bg-slate-900/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-2">Final Reminder</h4>
                    <p className="text-sm text-muted-foreground">
                      This disclaimer is an integral part of our legal framework and should be considered alongside our Terms and Conditions and Privacy Policy. Participation in PrizeDrop competitions indicates your acceptance of all these documents. If you do not agree with any part of this disclaimer, please discontinue use of our platform immediately.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}